import { html, LitElement, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import style from './ui-lugas-session-timer.css?inline';

const CName = 'ui-lugas-session-timer';

@customElement(CName)
export class UiLugasSessionTimer extends LitElement {
  static readonly styles = unsafeCSS(style);
  @state() diffMs: number | undefined;
  private _countDownTimeout: ReturnType<typeof setTimeout> | undefined;

  private readonly _handleSubscription = (since: Date | null) => {
    if (since) {
      this._setupTimeout(since);
    } else {
      this.diffMs = undefined;
      clearTimeout(this._countDownTimeout);
    }
  };

  protected override scheduleUpdate(): undefined | Promise<unknown> {
    if (!this.isConnected) return;
    return super.scheduleUpdate() as undefined | Promise<unknown>;
  }

  connectedCallback() {
    super.connectedCallback();
    window.$app.activationFlow.subscribeLastActivationState(this._handleSubscription);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._countDownTimeout);
    window.$app.activationFlow.unsubscribeLastActivationState(this._handleSubscription);
  }

  render() {
    if (this.diffMs === undefined) return nothing;
    return html`${this._renderElapsedTime(this.diffMs)}`;
  }

  private _renderElapsedTime(diffMs: number): TemplateResult | typeof nothing {
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const pad = (n: number): string => String(n).padStart(2, '0');
    return html`<p class="text">${I18n.lugasSessionTimer.activeSession} ${pad(hours)}<span class="colon">:</span>${pad(minutes)}</p>`;
  }

  private _getMillisecondsInMinute(date: Date): number {
    return date.getSeconds() * 1000 + date.getMilliseconds();
  }

  private _calculateMsToNextMinute(now: Date, since: Date): number {
    const msDifference = this._getMillisecondsInMinute(now) - this._getMillisecondsInMinute(since);
    return msDifference < 0 ? -msDifference : 60000 - msDifference;
  }

  private _setupTimeout(since: Date) {
    clearTimeout(this._countDownTimeout);
    if (!this.isConnected) return;
    const now = new Date();
    // diffMs can be smaller than 0 only in case the client time is behind the server time - thus cap to 0
    this.diffMs = Math.max(now.getTime() - since.getTime(), 0);
    this._countDownTimeout = setTimeout(() => this._setupTimeout(since), this._calculateMsToNextMinute(now, since));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiLugasSessionTimer;
  }
}
